import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import type { MonthlySpendingSummary as MonthlySpendingSummaryType } from "@/types";
import { Calendar } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface MonthlySpendingSummaryProps {
  data?: MonthlySpendingSummaryType;
  isLoading?: boolean;
  selectedMonth: string;
  onMonthChange: (month: string) => void;
}

/** Matches the server's synthetic bucket id (dashboard.service.ts, Q13). */
const UNCATEGORIZED_ID = -1;

export function MonthlySpendingSummary({
  data,
  isLoading,
  selectedMonth,
  onMonthChange,
}: MonthlySpendingSummaryProps) {
  const navigate = useNavigate();
  const [manualInput, setManualInput] = useState("");
  const [showManualInput, setShowManualInput] = useState(false);

  // Generate list of available months (last 24 months up to current month)
  const availableMonths = useMemo(() => {
    const months: Array<{ value: string; label: string; shortLabel: string }> = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    // Generate 24 months backwards from current month
    for (let i = 0; i < 24; i++) {
      const date = new Date(currentYear, currentMonth - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const value = `${year}-${String(month).padStart(2, "0")}`;
      const label = date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      const shortLabel = date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
      months.push({ value, label, shortLabel });
    }

    // Add custom option
    months.push({ value: "custom", label: "Custom date...", shortLabel: "Custom..." });

    return months;
  }, []);

  // Get current month short label
  const currentMonthLabel = useMemo(() => {
    const found = availableMonths.find((m) => m.value === selectedMonth);
    return found?.shortLabel || selectedMonth;
  }, [availableMonths, selectedMonth]);

  // Handle select change
  const handleSelectChange = (value: string) => {
    if (value === "custom") {
      setShowManualInput(true);
      setManualInput(selectedMonth);
    } else {
      setShowManualInput(false);
      onMonthChange(value);
    }
  };

  // Handle manual input change and validation
  const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setManualInput(value);

    // Validate YYYY-MM format
    const regex = /^\d{4}-(0[1-9]|1[0-2])$/;
    if (regex.test(value)) {
      onMonthChange(value);
    }
  };

  // Handle click on a category row to navigate to the transactions page.
  // The synthetic Uncategorized bucket (-1, Q13) has no category id to
  // filter by — the transactions page has no "no category" filter, so the
  // click just opens the month's window (same handling as the credit-card
  // category breakdown).
  const handleCategoryClick = (categoryId: number) => {
    const [year, month] = selectedMonth.split("-").map(Number);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);

    // Navigate to transactions page with filters
    const query = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
    if (categoryId !== UNCATEGORIZED_ID) query.set("categoryId", String(categoryId));
    navigate(`/transactions?${query.toString()}`);
  };

  if (isLoading || !data) {
    return (
      <div className="border border-border rounded-lg overflow-hidden bg-background p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="size-4 bg-muted rounded animate-pulse" />
            <div className="h-4 w-32 bg-muted rounded animate-pulse" />
          </div>
          <div className="h-8 w-24 bg-muted rounded-full animate-pulse" />
        </div>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton loader
            <div key={i} className="flex justify-between py-2">
              <div className="h-4 w-28 bg-muted rounded animate-pulse" />
              <div className="h-4 w-20 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-background">
      {/* Header */}
      <div className="border-b border-border bg-muted px-6 py-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium text-foreground">Summary</h2>

          {/* Compact Month Selector */}
          <Select
            value={showManualInput ? "custom" : selectedMonth}
            onValueChange={handleSelectChange}
          >
            <SelectTrigger className="h-8 w-auto gap-1 rounded-full border-border bg-muted px-3 text-xs font-medium text-muted-foreground hover:bg-muted">
              <SelectValue>{currentMonthLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent align="end">
              {availableMonths.map((month) => (
                <SelectItem key={month.value} value={month.value} className="text-sm">
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="p-6">
        {/* Manual Input Field - only shown when custom is selected */}
        {showManualInput && (
          <div className="flex flex-col gap-1.5 mb-4">
            <Label
              htmlFor="manual-month"
              className="text-xs text-muted-foreground flex items-center gap-1"
            >
              <Calendar className="size-3" />
              Custom (YYYY-MM)
            </Label>
            <Input
              id="manual-month"
              type="text"
              placeholder="2023-01"
              value={manualInput}
              onChange={handleManualInputChange}
              className="w-full sm:w-[200px]"
              pattern="\d{4}-(0[1-9]|1[0-2])"
            />
          </div>
        )}

        {/* Category List - Clean and Simple */}
        <div className="space-y-0">
          {data.categoriesSpending.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground/70">
              No spending data available
            </div>
          ) : (
            <>
              {data.categoriesSpending.slice(0, 8).map((category) => (
                <button
                  key={category.categoryName}
                  className="w-full flex items-center justify-between py-3 text-left hover:bg-muted -mx-2 px-2 rounded-lg"
                  onClick={() => handleCategoryClick(category.categoryId)}
                  type="button"
                >
                  <span className="text-xs font-medium text-muted-foreground uppercase">
                    {category.categoryName}
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    <AmountDisplay amount={category.amount} />
                  </span>
                </button>
              ))}
              {data.categoriesSpending.length > 8 && (
                <p className="pt-3 text-center text-xs text-muted-foreground/70">
                  +{data.categoriesSpending.length - 8} more
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
